import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CustomTable from "../../../components/CustomTable/CustomTable";
import { useAuth } from "../../../context/AuthContext";
import { useFeedBack } from "../../../context/FeedBackContext";
import useAxios from "../../../hooks/useAxios";
import useProviders from "../../../hooks/useProviders";
import SystemInfo from "../../../util/SystemInfo";
import useProductObservations from "../../../hooks/useProductObservations";
import ProductObservationsColumns from "../../../components/CustomTable/Columns/ProductObservationsColumns";

const ProductObservations = () => {

    const { user, permissions, isSuperAdmin } = useAuth();
    const { setCustomAlert, setLoading } = useFeedBack();

    const systemCode = SystemInfo?.systemCode || 'sys-002';
    const canDeleteAll = isSuperAdmin || permissions?.includes(`${systemCode}-delete-observations`);

    const isRecordOwner = (obs) => {
        return (
            (obs?.user_id && Number(obs?.user_id) === Number(user?.id)) ||
            (obs?.created_by && Number(obs?.created_by) === Number(user?.id)) ||
            (obs?.user?.id && Number(obs?.user?.id) === Number(user?.id)) ||
            (obs?.createdBy?.id && Number(obs?.createdBy?.id) === Number(user?.id))
        );
    };

    const [filters, setFilters] = useState({
        page: 1,
        guide_number: '',
        purchase_order_number: '',
        provider_id: '',
        start: '',
        end: ''
    });

    const [selectedValues, setSelectedValues] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    const [{ providers }, getProviders] = useProviders({ params: { perPage: 500 } }, { useCache: false });
    const [{ observations, total, numberOfPages, error: obsError, loading }, getObservations] = useProductObservations({ params: { ...filters }, options: { useCache: false } });
    const [{ error: deleteError, loading: deleteLoading }, deleteRecord] = useAxios({ method: 'DELETE' }, { manual: true, useCache: false });

    useEffect(() => {
        setLoading?.({
            show: deleteLoading,
            message: 'Eliminando Observación'
        });
    }, [deleteLoading]);

    useEffect(() => {
        if (deleteError) {
            setCustomAlert({
                title: 'error',
                severity: 'danger',
                message: 'Ha ocurrido un error al eliminar.',
                show: true
            });
        }

        if (obsError) {
            setCustomAlert({
                title: 'error',
                severity: 'danger',
                message: 'Ha ocurrido un error al obtener las observaciones.',
                show: true
            });
        }
    }, [deleteError, obsError]);

    useEffect(() => {
        if (selectAll) {
            const selectable = canDeleteAll
                ? observations?.map?.((value) => value?.id)
                : observations?.filter?.(isRecordOwner)?.map?.((value) => value?.id);
            setSelectedValues(selectable || []);
        } else {
            setSelectedValues([]);
        }
    }, [selectAll, observations, canDeleteAll, user]);

    const handleDelete = (value) => {
        deleteRecord({ url: `/product-observations/${value?.id}` }).then(() => {
            setCustomAlert({
                title: '¡Operación Exitosa!',
                severity: 'success',
                message: 'El registro ha sido eliminado exitosamente.',
                show: true
            });
            getObservations();
        });
    };

    const handleSelectALL = () => {
        setSelectAll((oldSelectAll) => !oldSelectAll);
    };

    const handleSelectValue = (selectedValue) => {
        // Solo permitir seleccionar si puede eliminar este registro
        if (!canDeleteAll && !isRecordOwner(selectedValue)) {
            setCustomAlert({
                title: 'Atención',
                severity: 'warning',
                message: 'Solo puedes seleccionar para eliminar tus propias observaciones.',
                show: true
            });
            return;
        }

        const value = selectedValues?.includes(Number(selectedValue?.id));
        if (value) {
            const newValues = selectedValues?.filter(n => n !== Number(selectedValue?.id));
            setSelectedValues(newValues);
        } else {
            setSelectedValues((oldSelectedValues) => [...oldSelectedValues, Number(selectedValue?.id)]);
        }
    };

    const handlePageChange = (page) => {
        setFilters((oldFilters) => ({
            ...oldFilters,
            page: page
        }));
    };

    const handleDeleteSelected = () => {
        deleteRecord({ url: `/product-observations/multiple`, data: { ids: selectedValues } }).then(() => {
            setCustomAlert({
                title: '¡Operación Exitosa!',
                severity: 'success',
                message: 'Los registros han sido eliminados exitosamente.',
                show: true
            });
            setSelectedValues([]);
            getObservations();
        });
    };

    const handleChange = (e) => {
        setFilters((oldFilters) => ({
            ...oldFilters,
            [e.target.name]: e.target.value,
            page: 1
        }));
    };

    return (
        <div>
            <div className="my-4 justify-content-end d-flex">
                <Link to={"/observaciones/crear"} className="btn btn-primary">
                    Crear Observación
                </Link>
            </div>

            <div className="row">
                <div className="col-md-3">
                    <div className="card p-4">
                        <label className="text-primary">
                            N° Guía / Factura
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            value={filters?.guide_number}
                            onChange={handleChange}
                            name="guide_number"
                            placeholder="N° Guía o Factura..."
                        />
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card p-4">
                        <label className="text-primary">
                            N° Orden de Compra
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            value={filters?.purchase_order_number}
                            onChange={handleChange}
                            name="purchase_order_number"
                            placeholder="N° Orden de Compra..."
                        />
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card p-4">
                        <label className="text-primary">
                            Proveedor
                        </label>
                        <select name="provider_id" className="form-control" value={filters?.provider_id} onChange={handleChange}>
                            <option value="">Todos los proveedores</option>
                            {
                                providers?.map((provider, i) => (
                                    <option key={i} value={provider?.id}>
                                        {provider?.name}
                                    </option>
                                ))
                            }
                        </select>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card p-4">
                        <label className="text-primary">
                            Fecha
                        </label>
                        <div className="row">
                            <div className="col-md-6 form-group">
                                <label className="text-primary small">
                                    Desde:
                                </label>
                                <input
                                    type="date"
                                    onChange={handleChange}
                                    className="form-control form-control-sm"
                                    name="start"
                                    value={filters?.start}
                                />
                            </div>
                            <div className="col-md-6 form-group">
                                <label className="text-primary small">
                                    Hasta:
                                </label>
                                <input
                                    type="date"
                                    onChange={handleChange}
                                    className="form-control form-control-sm"
                                    name="end"
                                    value={filters?.end}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <CustomTable
                onDeleteSelected={handleDeleteSelected}
                onSelectValue={handleSelectValue}
                onSelectAll={handleSelectALL}
                loading={loading}
                selectAll={selectAll}
                title={'Observaciones de Productos'}
                updatePath={"/observaciones"}
                entity={"product-observations"}
                updateOptionString={'Ver Detalles'}
                onDelete={handleDelete}
                selectedValues={selectedValues}
                pages={numberOfPages}
                total={total}
                values={observations}
                currentPage={filters?.page}
                collumns={ProductObservationsColumns}
                changePage={handlePageChange}
                filters={filters}
                excelUrl={'/product-observations/export/excel'}
                perPage={filters?.perPage}
                onPerPageChange={(e) => setFilters((oldFilters) => ({
                    ...oldFilters,
                    [e.target.name]: e.target.value,
                    page: 1
                }))}
            />
        </div>
    );
};

export default ProductObservations;
